import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppVersion } from "@/hooks/useAppVersion";
import { getOpenFormCount, subscribeOpenFormCount } from "@/hooks/useDraftAutosave";

const IDLE_MS = 5 * 60 * 1000; // 5 min sin input → idle
const HARD_DEADLINE_MS = 4 * 60 * 60 * 1000; // 4h en versión vieja → mostrar toast como último recurso

function isSafeMoment() {
  return getOpenFormCount() === 0;
}

/**
 * useDeferredReload — orquesta la recarga silenciosa cuando hay versión nueva.
 *
 * Estrategia:
 *  1. Cambio de ruta + sin formularios abiertos → recarga inmediata.
 *  2. Idle (5 min sin input) + sin formularios abiertos → recarga inmediata.
 *  3. Hard-deadline (4 h en versión vieja) → expone `showLastResortToast=true`
 *     para que el caller muestre un aviso explícito.
 *
 * Returns:
 *  - showLastResortToast: boolean
 *  - dismissLastResort(): suprime el toast por hoy
 *  - reloadNow(): fuerza recarga (botón del toast)
 */
export function useDeferredReload() {
  // `reload` from useAppVersion advances the stored version BEFORE reloading,
  // which is essential — otherwise after the reload the next mount detects the
  // same mismatch and we get an infinite reload loop.
  const { updateAvailable, dismiss, reload } = useAppVersion();
  const location = useLocation();
  const [showLastResortToast, setShowLastResortToast] = useState(false);
  const detectedAtRef = useRef(null);

  // `reload` se recrea en cada render de useAppVersion; lo guardamos en un ref
  // y exponemos un wrapper estable para poder agregarlo a las deps de los
  // useEffect sin disparar loops.
  const reloadRef = useRef(reload);
  useEffect(() => {
    reloadRef.current = reload;
  }, [reload]);
  const performReload = useCallback(() => {
    reloadRef.current();
  }, []);

  // Marca cuándo se detectó la nueva versión por primera vez.
  useEffect(() => {
    if (updateAvailable && !detectedAtRef.current) {
      detectedAtRef.current = Date.now();
    }
    if (!updateAvailable) {
      detectedAtRef.current = null;
      setShowLastResortToast(false);
    }
  }, [updateAvailable]);

  // Estrategia 1: cambio de ruta.
  useEffect(() => {
    if (!updateAvailable) return;
    if (isSafeMoment()) {
      performReload();
    }
  }, [location.pathname, updateAvailable, performReload]);

  // Estrategia 2: idle timer + reintento al cerrar el último form abierto.
  useEffect(() => {
    if (!updateAvailable) return undefined;

    let idleTimer = null;
    function resetIdle() {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        if (isSafeMoment()) performReload();
      }, IDLE_MS);
    }
    const events = ["mousemove", "keydown", "mousedown", "touchstart", "scroll"];
    for (const ev of events) window.addEventListener(ev, resetIdle, { passive: true });
    resetIdle();

    // También intenta recargar apenas se cierre el último formulario.
    // Pequeño delay para que cualquier guardado/POST en vuelo del cierre del
    // form alcance a despacharse antes de la recarga.
    let postCloseTimer = null;
    const unsub = subscribeOpenFormCount((count) => {
      if (count !== 0) return;
      if (postCloseTimer) clearTimeout(postCloseTimer);
      postCloseTimer = setTimeout(() => {
        if (isSafeMoment()) performReload();
      }, 1500);
    });

    return () => {
      if (idleTimer) clearTimeout(idleTimer);
      if (postCloseTimer) clearTimeout(postCloseTimer);
      for (const ev of events) window.removeEventListener(ev, resetIdle);
      unsub();
    };
  }, [updateAvailable, performReload]);

  // Estrategia 3: hard-deadline → toast de último recurso.
  useEffect(() => {
    if (!updateAvailable) return undefined;
    const id = setInterval(() => {
      if (!detectedAtRef.current) return;
      if (Date.now() - detectedAtRef.current > HARD_DEADLINE_MS) {
        setShowLastResortToast(true);
      }
    }, 60 * 1000);
    return () => clearInterval(id);
  }, [updateAvailable]);

  return {
    showLastResortToast,
    dismissLastResort: () => {
      setShowLastResortToast(false);
      dismiss();
    },
    reloadNow: performReload,
  };
}
